import React from 'react';
import { ItinerarySegment, ItinerarySegmentDetail } from '@kiwicom/orbit-components/lib/Itinerary';
import Airplane from '@kiwicom/orbit-components/lib/icons/Airplane';
import AirplaneDown from '@kiwicom/orbit-components/lib/icons/AirplaneDown';
import Bus from '@kiwicom/orbit-components/lib/icons/Bus';
import Entertainment from '@kiwicom/orbit-components/lib/icons/Entertainment';
import InformationCircle from '@kiwicom/orbit-components/lib/icons/InformationCircle';
import PowerPlug from '@kiwicom/orbit-components/lib/icons/PowerPlug';
import Train from '@kiwicom/orbit-components/lib/icons/Train';
import Wifi from '@kiwicom/orbit-components/lib/icons/Wifi';
import Seat from '@kiwicom/orbit-components/lib/icons/Seat';
import Badge from '@kiwicom/orbit-components/lib/Badge';
import Stack from '@kiwicom/orbit-components/lib/Stack';
import {
  SegmentGroupSegmentGroupTypeEnum,
  Segment as SegmentType,
  SegmentAdditionalInfoGroupItemIconEnum,
  ItineraryBadge,
} from '@kiwicom/openapi-sdk/mmb';
import { useLog } from '@kiwicom/nitro/lib/services/log/context';
import { Category, createEventLogUnified, ErrorAction } from '@/common/services/logging';

import Stop from './Stop';
import ItineraryBadgeList from './ItineraryBadgeList';
import formatDuration from '../../../services/helpers/formatDuration';
import HiddenStop from './HiddenStop';
import Separator from '@kiwicom/orbit-components/lib/Separator';
import ThrowAwayTicketBanner from './banners/ThrowAwayTicketBanner';
import FooterBadgesBanner from './banners/FooterBadgesBanner';
import HiddenCityBanner from './banners/HiddenCityBanner';
import Ticket from '@kiwicom/orbit-components/lib/icons/Ticket';
import Terminal from '@kiwicom/orbit-components/lib/icons/Terminal';
import BoardingGate from '@kiwicom/orbit-components/lib/icons/BoardingGate';

const AdditionalInfoItemIcon = ({ icon }: { icon: SegmentAdditionalInfoGroupItemIconEnum }) => {
  const { log } = useLog();
  switch (icon) {
    case SegmentAdditionalInfoGroupItemIconEnum.Info:
      return <InformationCircle size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Train:
      return <Train size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Airplane:
      return <Airplane size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Bus:
      return <Bus size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Seat:
      return <Seat size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Wifi:
      return <Wifi size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.PowerPlug:
      return <PowerPlug size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Entertainment:
      return <Entertainment size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Ticket:
      return <Ticket size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.Terminal:
      return <Terminal size="small" ariaHidden />;
    case SegmentAdditionalInfoGroupItemIconEnum.BoardingGate:
      return <BoardingGate size="small" ariaHidden />;
    default: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustivenessCheck: never = icon;

      void log(
        createEventLogUnified(
          Category.COMMON,
          ErrorAction.InvalidData,
          'segment additional info item icon',
        ),
        { receivedValue: icon },
      );
      return null;
    }
  }
};

const SegmentBanners = ({
  segment,
  footerBadges,
}: {
  segment: SegmentType;
  footerBadges?: ItineraryBadge[];
}) => {
  const hasTravelHackBanner =
    Boolean(segment.hiddenArrivalInfo?.badges) || Boolean(segment.throwAwayInfo?.badges);
  const hasFooterBanner = footerBadges && footerBadges.length > 0;
  return (
    <Stack spacing="300">
      {segment.throwAwayInfo?.badges && (
        <ThrowAwayTicketBanner throwAwayInfo={segment.throwAwayInfo} />
      )}
      {segment.hiddenArrivalInfo?.badges && (
        <HiddenCityBanner hiddenCityData={segment.hiddenArrivalInfo} />
      )}
      {hasTravelHackBanner && hasFooterBanner && <Separator />}
      {footerBadges && footerBadges.length > 0 && (
        <FooterBadgesBanner footerBadges={footerBadges} />
      )}
    </Stack>
  );
};

type SegmentProps = {
  segment: SegmentType;
  type: SegmentGroupSegmentGroupTypeEnum;
  footerBadges?: ItineraryBadge[];
};

/**
 * Component which renders single segment of Itinerary. Can contain two stops (arrival and departure) as well as
 * carrier info, duration, cabin class, connection number, layover badges and additional info about the journey.
 * Additional info will be visible only after expanding the component.
 *
 * @param segment - Segment object coming from backend
 * @param type - Type of the segment group which this segment belongs to (this will be used for styling
 * in case of a schedule change, and it's passed to Stop components)
 * @param footerBadges - Array of itinerary badges ({@link ItineraryBadge}) which will be rendered at the end of the segment
 * @returns Segment component
 */
const Segment = ({ segment, type, footerBadges }: SegmentProps) => {
  const hasBanner =
    (footerBadges && footerBadges.length > 0) ||
    Boolean(segment.hiddenArrivalInfo?.badges) ||
    Boolean(segment.throwAwayInfo?.badges);
  return (
    <>
      <ItinerarySegment
        noElevation={type !== SegmentGroupSegmentGroupTypeEnum.Default}
        actionable={Boolean(segment.additionalInfoGroups)}
        banner={hasBanner && <SegmentBanners segment={segment} footerBadges={footerBadges} />}
        spaceAfter={type === SegmentGroupSegmentGroupTypeEnum.Default ? 'normal' : 'none'}
        dataTest="ItinerarySegment"
      >
        <Stop stop={segment.departure} type={type} />
        <ItinerarySegmentDetail
          icon={<AirplaneDown size="small" ariaHidden />}
          duration={formatDuration(segment.duration.value)}
          summary={
            <Stack wrap spacing="100">
              {segment.carrier && (
                <Badge
                  carriers={[
                    {
                      code: segment.carrier.carrierIconId,
                      name: segment.carrier.name,
                    },
                  ]}
                >
                  {segment.carrier.name}
                </Badge>
              )}
              {segment.connectionNumber && (
                <Badge
                  type={
                    segment.connectionNumber.originalValues &&
                    type !== SegmentGroupSegmentGroupTypeEnum.Default &&
                    type !== SegmentGroupSegmentGroupTypeEnum.Neutral
                      ? `${type}Subtle`
                      : 'neutral'
                  }
                >
                  {segment.connectionNumber.value}
                </Badge>
              )}
              {segment.cabinClass && <Badge>{segment.cabinClass.value}</Badge>}
            </Stack>
          }
          content={segment.additionalInfoGroups?.map(group => ({
            ...group,
            items: group.items.map(item => ({
              ...item,
              icon: <AdditionalInfoItemIcon icon={item.icon} />,
            })),
          }))}
        />
        <Stop stop={segment.arrival} type={type} hidden={Boolean(segment.hiddenArrivalInfo)} />
        {segment.hiddenArrivalInfo && <HiddenStop hiddenArrivalInfo={segment.hiddenArrivalInfo} />}
      </ItinerarySegment>
      {segment.layover?.badges && <ItineraryBadgeList badges={segment.layover.badges} />}
    </>
  );
};

export default Segment;
